import { Injectable, inject, computed } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Loader } from '@googlemaps/js-api-loader';
import { TranslateService } from '@ngx-translate/core';
import { RestService } from 'src/app/services/rest.service';
import { RegisterUserFormService } from 'src/app/pages/register-user/services/register-user-form.service';
import { BreakpointsService } from 'src/app/services/breakpoints.service';
import { FormStepperService } from 'src/app/pages/register-user/components/register-user-form-mobile/services/form-stepper.service';
import { closeAllCiSnackbar } from '@iurban-malaga/cicerone-ui';

export interface LocationData {
  lat: number;
  lng: number;
  address: string;
}

export interface GooglePlaceResult {
  latitude: number;
  longitude: number;
  name: string;
}

@Injectable({
  providedIn: 'root'
})
export class LocationFieldService {
  // Services
  private _translateService = inject(TranslateService);
  private _restService = inject(RestService);
  private _registerUserFormService = inject(RegisterUserFormService);
  private _formStepperService = inject(FormStepperService);
  private _breakpointsService = inject(BreakpointsService);

  // Google Maps configuration
  private readonly _GOOGLE_MAPS_API_KEY = 'AIzaSyCosmIo9py_6Dfd_mr1CWSJxQtDzl1LWjA';
  private readonly _SEARCH_RADIUS_KM = 0.06; // 6km in degrees
  private readonly _SUPPORTED_COUNTRIES = ['es', 'and', 'br'];

  // Computed properties from services
  private _startLocation = this._registerUserFormService.startLocation;
  private _isNextButtonClicked = this._formStepperService.isNextButtonClicked;
  private _isMobile = this._breakpointsService.isMobile;
  private _markAllFormFieldsAsTouched = this._registerUserFormService.markAllFormFieldsAsTouched;

  /**
   * Gets the current start location from the form service
   */
  get startLocation() {
    return this._startLocation;
  }

  /**
   * Gets the current end location setting from the form service
   */
  get endLocation() {
    return this._registerUserFormService.endLocation;
  }

  /**
   * Initializes Google Maps Autocomplete for the given input element
   * @param inputElement - The HTML input element to attach autocomplete to
   * @param onPlaceSelected - Callback function when a place is selected
   */
  async initializeAutocomplete(
    inputElement: HTMLInputElement,
    onPlaceSelected: (location: GooglePlaceResult) => void
  ): Promise<void> {
    try {
      // Set placeholder text
      inputElement.placeholder = this._translateService.instant('Seleccionar punto de inicio');
      
      // Configure search bounds based on device location
      const searchBounds = this._createSearchBounds();
      const autocompleteOptions = this._createAutocompleteOptions(searchBounds);

      // Load Google Maps API
      const loader = new Loader({
        apiKey: this._GOOGLE_MAPS_API_KEY,
        version: 'weekly',
        libraries: ['geometry', 'places']
      });

      await loader.load();
      const { Autocomplete } = await google.maps.importLibrary('places') as google.maps.PlacesLibrary;
      
      // Create and configure autocomplete
      const autocomplete = new Autocomplete(inputElement, autocompleteOptions);
      
      // Handle place selection
      autocomplete.addListener('place_changed', async () => {
        const place = autocomplete.getPlace();
        try {
          const location = await this._extractLocationFromPlace(place);
          onPlaceSelected(location);
        } catch (error) {
          console.error('Error processing selected place:', error);
        }
      });

    } catch (error) {
      console.error('Error initializing Google Maps Autocomplete:', error);
      throw new Error('Failed to initialize location autocomplete');
    }
  }

  /**
   * Updates the start location in the form service and related form controls
   * @param location - The location data to set
   * @param form - The form group to update
   */
  updateStartLocation(location: GooglePlaceResult, form: FormGroup): void {
    // Update form service
    this._registerUserFormService.setStartLocation({
      lat: location.latitude,
      lng: location.longitude,
      address: location.name
    });

    // Update form controls
    this._updateLocationFormControls(form, location);

    // Close mobile snackbars for better UX
    if (this._isMobile()) {
      closeAllCiSnackbar();
    }
  }

  /**
   * Clears the start location from both service and form
   * @param form - The form group to clear
   */
  clearStartLocation(form: FormGroup): void {
    this._registerUserFormService.setStartLocation(null);
    
    form.get('startLocationLat')?.setValue(null);
    form.get('startLocationLng')?.setValue(null);
  }

  /**
   * Updates the end location setting
   * @param isEnabled - Whether end location is enabled
   * @param form - The form group to update
   */
  updateEndLocationSetting(isEnabled: boolean, form: FormGroup): void {
    this._registerUserFormService.setEndLocation(isEnabled);
    form.get('endLocation')?.setValue(isEnabled);
  }

  /**
   * Validates if the location field is invalid based on current state
   * @param control - The form control to validate
   * @returns True if the field is invalid and should show error
   */
  isLocationFieldInvalid(control: FormControl | null): boolean {
    if (!control) return false;
    
    // If location is set in service, field is valid
    if (this._startLocation()?.address) return false;
    
    if (this._isMobile()) {
      // Mobile: validate only when next button is clicked
      return this._isNextButtonClicked() && 
             control.hasError('required') && 
             (control.touched || control.dirty);
    } else {
      // Desktop: normal validation behavior
      return control.hasError('required') && 
             (control.touched || this._markAllFormFieldsAsTouched());
    }
  }

  /**
   * Checks if the end location switch should be disabled
   * @param form - The form group to check
   * @returns True if the switch should be disabled
   */
  isEndLocationSwitchDisabled(form: FormGroup): boolean {
    return !form.get('startLocationAddress')?.value;
  }

  /**
   * Gets the current start location address
   * @returns The current address or empty string
   */
  getCurrentStartLocationAddress(): string {
    return this._startLocation()?.address || '';
  }

  /**
   * Synchronizes location data between form control and service
   * @param controlValue - The value from the form control
   * @param currentServiceValue - The current value in the service
   */
  synchronizeLocationData(controlValue: string, currentServiceValue: LocationData | null): void {
    if (controlValue && controlValue !== currentServiceValue?.address) {
      this._registerUserFormService.setStartLocation({
        lat: currentServiceValue?.lat || 0,
        lng: currentServiceValue?.lng || 0,
        address: controlValue
      });
    }
  }

  // Private methods

  /**
   * Creates search bounds based on device location
   * @returns Google Maps LatLngBounds literal
   */
  private _createSearchBounds(): google.maps.LatLngBoundsLiteral {
    const center = this._restService.device.location;
    const radius = this._SEARCH_RADIUS_KM;
    
    return {
      north: center.latitude + radius,
      south: center.latitude - radius,
      east: center.longitude + radius,
      west: center.longitude - radius
    };
  }

  /**
   * Creates autocomplete options configuration
   * @param bounds - The search bounds to use
   * @returns Autocomplete options object
   */
  private _createAutocompleteOptions(bounds: google.maps.LatLngBoundsLiteral): google.maps.places.AutocompleteOptions {
    return {
      bounds,
      strictBounds: true,
      types: [],
      componentRestrictions: { 
        country: this._SUPPORTED_COUNTRIES 
      }
    };
  }

  /**
   * Extracts location data from Google Places result
   * @param place - The Google Places result
   * @returns Promise resolving to location data
   */
  private async _extractLocationFromPlace(place: google.maps.places.PlaceResult): Promise<GooglePlaceResult> {
    return new Promise((resolve, reject) => {
      if (place?.geometry?.location) {
        const location: GooglePlaceResult = {
          latitude: place.geometry.location.lat(),
          longitude: place.geometry.location.lng(),
          name: place.name || place.formatted_address || ''
        };
        resolve(location);
      } else {
        reject(new Error('No se pudo obtener la geolocalización del lugar seleccionado.'));
      }
    });
  }

  /**
   * Validates if a location has all required data
   * @param location - The location data to validate
   * @returns True if location is complete and valid
   */
  isValidLocation(location: LocationData | null): boolean {
    return !!(location?.lat && location?.lng && location?.address?.trim());
  }

  /**
   * Calculates the distance between two coordinates using Haversine formula
   * @param lat1 - First latitude
   * @param lng1 - First longitude  
   * @param lat2 - Second latitude
   * @param lng2 - Second longitude
   * @returns Distance in kilometers
   */
  calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371; // Earth's radius in kilometers
    const dLat = this._degreesToRadians(lat2 - lat1);
    const dLng = this._degreesToRadians(lng2 - lng1);
    
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(this._degreesToRadians(lat1)) * Math.cos(this._degreesToRadians(lat2)) *
              Math.sin(dLng / 2) * Math.sin(dLng / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  /**
   * Checks if a location is within the supported search radius
   * @param location - The location to check
   * @returns True if location is within bounds
   */
  isLocationWithinBounds(location: LocationData): boolean {
    const center = this._restService.device.location;
    const distance = this.calculateDistance(
      center.latitude, 
      center.longitude, 
      location.lat, 
      location.lng
    );
    
    // Convert radius from degrees to approximate kilometers (rough conversion)
    const radiusKm = this._SEARCH_RADIUS_KM * 111; // 1 degree ≈ 111 km
    return distance <= radiusKm;
  }

  /**
   * Resets all location-related data
   * @param form - The form group to reset
   */
  resetAllLocationData(form: FormGroup): void {
    this.clearStartLocation(form);
    this.updateEndLocationSetting(false, form);
  }

  // Private utility methods

  /**
   * Converts degrees to radians
   * @param degrees - The degree value to convert
   * @returns The radian equivalent
   */
  private _degreesToRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  /**
   * Updates location-related form controls
   * @param form - The form group to update
   * @param location - The location data to set
   */
  private _updateLocationFormControls(form: FormGroup, location: GooglePlaceResult): void {
    form.get('startLocationAddress')?.setValue(location.name, { emitEvent: false });
    form.get('startLocationLat')?.setValue(location.latitude);
    form.get('startLocationLng')?.setValue(location.longitude);
  }
}
